import { Building2, Check, ChevronDown, ExternalLink } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { useEnterWorkspaceElsewhere } from "@/hooks/use-enter-workspace-elsewhere";
import { awayStore, returnHome } from "@/lib/lti/instance-switch";
import type { SocketScope } from "@/lib/socket-owner";
import { coreStore } from "@/lib/stores/core-store";
import { opsUiStore } from "@/lib/stores/ops-ui-store";
import { uiStore } from "@/lib/stores/ui-store";

/**
 * Which client's workspace selection this selector drives. `/operations` holds
 * its own `currentWorkspaceId` in `ops-ui-store`, because it offers only the
 * workspaces a tool can be opened in, so it must not write the Learner Client's,
 * and it must not go through `coreStore.selectWorkspace`, whose tail is `/app`'s
 * sequential connect chain.
 * The ops route runs its own workspace preamble off this value instead.
 */
export function WorkspaceSelector({
	scope = "app",
}: {
	scope?: SocketScope;
} = {}) {
	const isOps = scope === "operations";
	const workspacesMap = useStore(coreStore, (s) => s.workspaces);
	const opsWorkspaceIds = useStore(coreStore, (s) => s.opsWorkspaceIds);
	// `/app` offers every Workspace the caller participates in, which is the
	// question its gate asks (TECH-3260). `/operations` offers only the ones a
	// tool can be opened in, which is the question ITS gate asks (TECH-3284).
	// Intersected here rather than filtered into the store, because the map is
	// shared with `/app` and patched a row at a time by `workspace-announce`.
	const workspaces = useMemo(() => {
		const all = Array.from(workspacesMap.values());
		if (!isOps) return all;
		if (!opsWorkspaceIds) return [];
		const eligible = new Set(opsWorkspaceIds);
		return all.filter((w) => eligible.has(w.id));
	}, [workspacesMap, isOps, opsWorkspaceIds]);
	const appWorkspaceId = useStore(uiStore, (s) => s.currentWorkspaceId);
	const opsWorkspaceId = useStore(opsUiStore, (s) => s.currentWorkspaceId);
	const setOpsWorkspaceId = useStore(
		opsUiStore,
		(s) => s.setCurrentWorkspaceId
	);
	const selectWorkspace = useStore(coreStore, (s) => s.selectWorkspace);
	const away = useStore(awayStore, (s) => s.away);
	const homeWorkspaces = isOps ? [] : (away?.homeWorkspaces ?? []);

	const returnTo = async (workspaceId: string) => {
		const outcome = await returnHome(workspaceId);
		if (!outcome.ok) toast.error(outcome.message);
	};

	const allElsewhere = useStore(coreStore, (s) => s.workspacesElsewhere);
	const workspacesElsewhere = isOps ? [] : allElsewhere;
	const { select: selectElsewhere, consentDialog } =
		useEnterWorkspaceElsewhere();

	const currentWorkspaceId = isOps ? opsWorkspaceId : appWorkspaceId;
	const onSelectWorkspace = isOps ? setOpsWorkspaceId : selectWorkspace;

	const currentWorkspace = useMemo(
		() => workspaces.find((w) => w.id === currentWorkspaceId),
		[workspaces, currentWorkspaceId]
	);

	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const Wrapper = isCollapsed ? "div" : Card;

	return (
		<Wrapper className={isCollapsed ? "p-2" : "p-2"}>
			<SidebarMenu className={isCollapsed ? "" : "min-w-0 flex-1"}>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton asChild>
								<Button
									className="w-full justify-start"
									data-tour="workspace-selector"
									variant="ghost"
								>
									<Building2 className="size-5" />
									<span className="truncate">
										{currentWorkspace?.name ?? "Select Workspace"}
									</span>
									<ChevronDown className="ml-auto size-3.5" />
								</Button>
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start" className="w-56">
							<DropdownMenuLabel>Workspaces</DropdownMenuLabel>
							<DropdownMenuSeparator />
							{workspaces.length === 0 ? (
								<DropdownMenuItem disabled>
									No workspaces available
								</DropdownMenuItem>
							) : (
								workspaces.map((workspace) => (
									<DropdownMenuItem
										className={
											workspace.id === currentWorkspaceId ? "bg-accent" : ""
										}
										key={workspace.id}
										onClick={() => {
											if (workspace.id !== currentWorkspaceId) {
												onSelectWorkspace(workspace.id);
											}
										}}
									>
										<Building2 className="size-3.5" />
										{workspace.hostOrganization ? (
											<span className="flex min-w-0 flex-col">
												<span className="truncate">{workspace.name}</span>
												<span className="truncate text-muted-foreground text-xs">
													{workspace.hostOrganization.name}
												</span>
											</span>
										) : (
											workspace.name
										)}
										{workspace.id === currentWorkspaceId && (
											<Check className="ml-auto size-3.5" />
										)}
									</DropdownMenuItem>
								))
							)}
							{workspacesElsewhere.length > 0 && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuLabel>
										On another ELASO Instance
									</DropdownMenuLabel>
									{workspacesElsewhere.map((entry) => (
										<DropdownMenuItem
											key={`elsewhere:${entry.connectionId}`}
											onClick={() => selectElsewhere(entry)}
										>
											<ExternalLink className="size-3.5" />
											<span className="flex min-w-0 flex-col">
												<span className="truncate">{entry.workspaceName}</span>
												<span className="truncate text-muted-foreground text-xs">
													{entry.hostOrganizationName} ·{" "}
													{new URL(entry.hostInstanceUrl).host}
												</span>
											</span>
										</DropdownMenuItem>
									))}
								</>
							)}
							{homeWorkspaces.length > 0 && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuLabel>Your ELASO Instance</DropdownMenuLabel>
									{homeWorkspaces.map((workspace) => (
										<DropdownMenuItem
											key={`home:${workspace.id}`}
											onClick={() => returnTo(workspace.id)}
										>
											<Building2 className="size-3.5" />
											{workspace.name}
										</DropdownMenuItem>
									))}
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
			{consentDialog}
		</Wrapper>
	);
}
